import {useAuth} from '../stores/AuthStore'

// CampMinder employees log in with an @campminder.com address, so we treat that
// domain as the "internal user" signal for showing internal-only affordances
// (e.g. the "See Facial Recognition Confidence" photo action and album-level tag
// stats).
//
// This is a best-effort, CLIENT-SIDE VISIBILITY gate, NOT a security boundary.
// It is a tighter proxy for "CampMinder employee" than the multi-camp
// (clients > 1) heuristic, which also matched non-employee multi-camp admins.
//
// WHETHER A SERVER-SIDE BACKSTOP EXISTS IS PER-ENDPOINT — do not assume this
// gate "can never leak data":
//   - "See Facial Recognition Confidence" / album tag stats DO have one: the
//     faces backend enforces the authoritative `campminderstaff` gate and
//     returns 403 to a non-staff caller, so showing the affordance to a
//     non-employee exposes nothing.
//   - "Tagged Campers" (AB#23610) does NOT: `/photos/person-tags/list` grants
//     `clientemployee`/`system`, not `campminderstaff`, so any camp-admin token
//     can already call that endpoint directly. There the gate is visibility
//     only (accepted deliberately — see the AB#23610 plan, D2).
// Before reusing this hook to gate a new affordance, check the target
// endpoint's own policy rather than inheriting either assumption.
//
// It keys off `identityEmail` — the Auth0 LOGIN-IDENTITY email captured at
// login — NOT `person.email`. `person` is the camp-scoped persons-service record
// for the currently selected client, so its email is per-camp and frequently
// blank; keying off it made the gate flicker for employees active in multiple
// camps (true in one camp, false in another). `identityEmail` is constant across
// camp switches.
//
// Fail-closed: a missing/blank identity email is treated as not-an-employee.
const CAMPMINDER_EMAIL_DOMAIN = '@campminder.com'

export const useIsCampMinderEmployee = (): boolean => {
  const {identityEmail} = useAuth()
  return (identityEmail ?? '')
    .trim()
    .toLowerCase()
    .endsWith(CAMPMINDER_EMAIL_DOMAIN)
}
